/**
 * API 请求客户端
 * 基于 Fetch API 封装，提供统一的请求方法和错误处理
 */

import { getApiBaseUrl } from '@/utils/env';
import type { ApiResult, ApiErrorResponse } from './types/common';

// 用于重定向的函数
let redirectToLogin: (() => void) | null = null;

// 设置重定向函数
export function setRedirectToLogin(fn: () => void) {
  redirectToLogin = fn;
}

// 获取重定向函数
export function getRedirectToLogin() {
  return redirectToLogin;
}

/**
 * 请求配置选项
 */
export interface RequestConfig extends RequestInit {
  /** 是否需要认证（Bearer Token） */
  requiresAuth?: boolean;
  /** 是否需要 API Key 认证 */
  requiresApiKey?: boolean;
  /** API Key（如果未设置则从 localStorage 获取） */
  apiKey?: string;
  /**
   * 额外被视为"业务成功"的 HTTP 状态码白名单。
   *
   * 用于处理后端按 REST 语义返回非 2xx、但响应体仍是 `{ok:true,data:...}` 的场景
   * （典型例子：`POST /subscriptions/subscribe` 余额不足时返回 HTTP 402 + `pending_order`）。
   *
   * 规则：
   * - 状态码命中白名单且响应体 `ok===true`：按成功返回 ApiResult，不 throw
   * - 状态码命中白名单但响应体 `ok===false`：仍按错误 throw（错误依然暴露给调用方）
   * - 401 始终强制登出，不受白名单影响
   */
  acceptHttpStatus?: number[];
}

/**
 * API 错误类
 */
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * 获取存储的 JWT Token
 * 优先从 localStorage 直接获取，如果没有则从 Zustand store 中获取
 */
function getAuthToken(): string | null {
  // 尝试直接从 localStorage 获取
  let token = localStorage.getItem('access_token');
  
  // 如果没有，尝试从 Zustand auth store 中获取
  if (!token) {
    try {
      const stored = localStorage.getItem('storage-auth-storage');
      if (stored) {
        const parsed = JSON.parse(stored);
        token = parsed.state?.accessToken || null;
        
        // 如果找到了 token，也保存到 access_token 以便下次直接使用
        if (token) {
          localStorage.setItem('access_token', token);
        }
      }
    } catch (e) {
      console.error('[API Client] Failed to parse auth storage:', e);
    }
  }
  
  return token;
}

/**
 * 获取存储的 API Key
 */
function getStoredApiKey(): string | null {
  return localStorage.getItem('api_key');
}

/**
 * 构建完整的 URL
 */
function buildUrl(endpoint: string): string {
  const baseUrl = getApiBaseUrl();
  // 确保 baseUrl 不以 / 结尾，endpoint 以 / 开头
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${cleanBaseUrl}${cleanEndpoint}`;
}

/**
 * 构建请求头
 */
function buildHeaders(config: RequestConfig): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(config.headers as Record<string, string>),
  };

  // 添加 Bearer Token 认证
  if (config.requiresAuth) {
    const token = getAuthToken();
    console.log('[API Client] Auth check:', {
      requiresAuth: config.requiresAuth,
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'none',
    });
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.warn('[API Client] No token found for authenticated request!');
    }
  }

  // 添加 API Key 认证
  if (config.requiresApiKey) {
    const apiKey = config.apiKey || getStoredApiKey();
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }
  }

  return headers;
}

/**
 * 处理响应
 */
async function handleResponse<T>(
  response: Response,
  config: RequestConfig = {}
): Promise<ApiResult<T>> {
  // 尝试解析 JSON
  let data: any;
  try {
    data = await response.json();
  } catch (parseError) {
    // 如果不是 JSON 格式，抛出 HTTP 错误
    throw new ApiError(
      'INVALID_RESPONSE',
      `HTTP ${response.status}: ${response.statusText}`,
      response.status
    );
  }

  if (!response.ok) {
    // 401 始终强制登出，不受 acceptHttpStatus 影响
    if (response.status === 401) {
      console.error('[API Client] 401 Unauthorized - clearing auth and redirecting to login');
      
      // 清除所有认证信息
      clearAuthToken();
      
      // 清除 Zustand auth store
      try {
        const { useAuthStore } = await import('../stores/modules/auth');
        useAuthStore.getState().logout();
      } catch (e) {
        console.error('[API Client] Failed to clear auth store:', e);
      }
      
      // 如果当前不在登录页，才执行重定向
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && redirectToLogin) {
        setTimeout(() => {
          redirectToLogin?.();
        }, 0);
      }
      
      throw new ApiError(
        'UNAUTHORIZED',
        '认证已失效，请重新登录',
        response.status
      );
    }

    // acceptHttpStatus 白名单：非 2xx 但业务成功（body.ok === true）时按成功返回
    // 典型场景：POST /subscriptions/subscribe 余额不足返回 HTTP 402 + { ok: true, data: pending_order }
    if (
      config.acceptHttpStatus?.includes(response.status) &&
      data &&
      typeof data === 'object' &&
      data.ok === true
    ) {
      return data as ApiResult<T>;
    }

    // 其他错误（包括白名单状态码但 body.ok === false 的情况）
    const errorData = data as ApiErrorResponse;
    throw new ApiError(
      errorData.error?.code || 'HTTP_ERROR',
      errorData.error?.message || `请求失败 (${response.status})`,
      response.status
    );
  }

  // 成功响应
  return data as ApiResult<T>;
}

/**
 * 通用请求方法
 */
async function request<T = any>(
  endpoint: string,
  config: RequestConfig = {}
): Promise<ApiResult<T>> {
  try {
    const url = buildUrl(endpoint);
    const headers = buildHeaders(config);

    console.log('[API Client] Request:', {
      method: config.method || 'GET',
      url,
      requiresAuth: config.requiresAuth,
      hasAuthorization: !!(headers as Record<string, string>)['Authorization'],
      authHeaderPreview: (headers as Record<string, string>)['Authorization'] 
        ? `${String((headers as Record<string, string>)['Authorization']).substring(0, 30)}...` 
        : 'none',
    });

    // 从 config 中提取标准 fetch 选项，排除自定义属性
    const { requiresAuth, requiresApiKey, apiKey, acceptHttpStatus, ...fetchOptions } = config;

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    return await handleResponse<T>(response, config);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // 网络错误或其他异常
    throw new ApiError(
      'NETWORK_ERROR',
      error instanceof Error ? error.message : '网络请求失败'
    );
  }
}

/**
 * GET 请求
 * @param endpoint - API 端点路径
 * @param config - 请求配置
 */
export async function get<T = any>(
  endpoint: string,
  config?: RequestConfig
): Promise<ApiResult<T>> {
  return request<T>(endpoint, { ...config, method: 'GET' });
}

/**
 * POST 请求
 * @param endpoint - API 端点路径
 * @param data - 请求体数据
 * @param config - 请求配置
 */
export async function post<T = any>(
  endpoint: string,
  data?: any,
  config?: RequestConfig
): Promise<ApiResult<T>> {
  return request<T>(endpoint, {
    ...config,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PUT 请求
 * @param endpoint - API 端点路径
 * @param data - 请求体数据
 * @param config - 请求配置
 */
export async function put<T = any>(
  endpoint: string,
  data?: any,
  config?: RequestConfig
): Promise<ApiResult<T>> {
  return request<T>(endpoint, {
    ...config,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PATCH 请求
 * @param endpoint - API 端点路径
 * @param data - 请求体数据
 * @param config - 请求配置
 */
export async function patch<T = any>(
  endpoint: string,
  data?: any,
  config?: RequestConfig
): Promise<ApiResult<T>> {
  return request<T>(endpoint, {
    ...config,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * DELETE 请求
 * @param endpoint - API 端点路径
 * @param config - 请求配置
 */
export async function del<T = any>(
  endpoint: string,
  config?: RequestConfig
): Promise<ApiResult<T>> {
  return request<T>(endpoint, { ...config, method: 'DELETE' });
}

/**
 * 设置认证 Token
 * @param token - JWT Token
 */
export function setAuthToken(token: string): void {
  localStorage.setItem('access_token', token);
}

/**
 * 清除认证 Token
 */
export function clearAuthToken(): void {
  localStorage.removeItem('access_token');
}

/**
 * 设置 API Key
 * @param apiKey - API Key
 */
export function setApiKey(apiKey: string): void {
  localStorage.setItem('api_key', apiKey);
}

/**
 * 清除 API Key
 */
export function clearApiKey(): void {
  localStorage.removeItem('api_key');
}